// FREEFORM (lasso) shape mode — an ADJUSTABLE, multi-click polygon outline that
// fills the 2D area it encloses with a one-voxel-thick layer (extrude afterwards
// for height). Good for organic footprints (a curved machine base, an
// elliptical, …) that a box or disc can't describe.
//
// Unlike the box/circle shapes (one press-drag-release), freeform is built point
// by point, borrowing the wall-draw lasso convention: each click drops a vertex,
// a rubber-band segment previews to the cursor, clicking the FIRST vertex (or
// pressing Enter) closes the loop and fills, and clicking an existing vertex
// grabs it so you can nudge it before committing — so you no longer have to nail
// the whole outline in a single pass. ESC cancels; Backspace removes the last
// point.
//
// The in-progress polygon lives here as module state (`poly`), independent of the
// `interaction` gesture union: building spans many clicks, and only the closing
// fill opens a transaction. The desktop input layer drives it (projects the
// cursor onto the fixed drawing plane, routes press/release); VR does not yet.
// On close, the path auto-closes and a cell fills iff the MAJORITY of its area
// lies inside the outline (sampled on a subgrid), reusing BOX/CIRCLE's
// reconcileFill bookkeeping.

import type * as THREE from "three";
import { CELL_SIZE, type PaletteIndex, type VoxelGrid } from "../../model/voxel-grid";
import { type CellBounds, type PickTarget } from "../pick";
import {
  type Cell,
  type ShapeFillState,
  axisHi,
  axisLo,
  planeFromPick,
  reconcileFill,
  setAxis,
} from "./shape-fill";

type Action = "draw" | "erase" | "recolor";

interface FreeformPoly {
  action: Action;
  planeAxis: 0 | 1 | 2;
  planeCoord: number;
  planeNormalSign: -1 | 1;
  grid: VoxelGrid; // target object's grid (resolved at the first vertex)
  group: THREE.Object3D; // target object's frame (preview parent + plane matrix)
  verts: Array<{ u: number; v: number }>; // placed vertices (sub-cell), in order
  cursor: { u: number; v: number } | null; // live pointer on the plane (rubber-band end)
  hoverVert: number | null; // vertex under the cursor (grab/close target + highlight)
  dragVert: number | null; // vertex being dragged this press
  controllerIndex: number;
}

let poly: FreeformPoly | null = null;

// Subgrid resolution per cell for the majority-coverage test on commit (N×N).
const SUB = 4;
// A vertex within this many cells of the cursor is "hovered" — close enough to
// grab (drag) or, for the first vertex, close the loop. Plane-space (so it's
// independent of screen projection); generous because Enter also closes.
const HOVER_CELLS = 0.75;

// The two in-plane axes for a given plane normal (same mapping the disc uses).
const inPlaneAxes = (planeAxis: 0 | 1 | 2): [0 | 1 | 2, 0 | 1 | 2] => [
  planeAxis === 0 ? 1 : 0,
  planeAxis === 2 ? 1 : 2,
];

const comp = (px: number, py: number, pz: number, axis: 0 | 1 | 2): number =>
  axis === 0 ? px : axis === 1 ? py : pz;

const clamp = (x: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, x));

// A plane hit (gridGroup-local metres) → continuous (sub-cell) plane coords,
// clamped to the canvas footprint.
function toUV(
  px: number,
  py: number,
  pz: number,
  bounds: CellBounds,
): { u: number; v: number } {
  if (!poly) return { u: 0, v: 0 };
  const [uAxis, vAxis] = inPlaneAxes(poly.planeAxis);
  const u = clamp(
    comp(px, py, pz, uAxis) / CELL_SIZE,
    axisLo(uAxis, bounds),
    axisHi(uAxis, bounds),
  );
  const v = clamp(
    comp(px, py, pz, vAxis) / CELL_SIZE,
    axisLo(vAxis, bounds),
    axisHi(vAxis, bounds),
  );
  return { u, v };
}

export function freeformPolyActive(): boolean {
  return poly !== null;
}

// The fixed drawing plane the desktop driver projects the cursor onto each frame.
export function freeformPolyPlane(): { planeAxis: 0 | 1 | 2; planeCoord: number } | null {
  return poly ? { planeAxis: poly.planeAxis, planeCoord: poly.planeCoord } : null;
}

// The target object's frame — the preview re-parents here and the desktop driver
// builds the plane matrix from it (the gesture object is only valid mid-press).
export function freeformPolyGroup(): THREE.Object3D | null {
  return poly ? poly.group : null;
}

export function freeformDragVert(): number | null {
  return poly ? poly.dragVert : null;
}

export function freeformHoverVertex(): number | null {
  return poly ? poly.hoverVert : null;
}

// Begin a new polygon: fix the drawing plane (and the object it draws into) from
// the press-time pick. The first vertex is dropped separately (at the press
// point), so it lands exactly under the cursor. Returns false if the pick has no
// surface/voxel to anchor on.
export function beginFreeformPoly(
  pick: PickTarget,
  controllerIndex: number,
  action: Action,
  grid: VoxelGrid,
  group: THREE.Object3D,
): boolean {
  const plane = planeFromPick(pick, action);
  if (!plane) return false;
  poly = {
    action,
    planeAxis: plane.planeAxis,
    planeCoord: plane.planeCoord,
    planeNormalSign: plane.planeNormalSign,
    grid,
    group,
    verts: [],
    cursor: null,
    hoverVert: null,
    dragVert: null,
    controllerIndex,
  };
  return true;
}

export function dropFreeformVertex(
  px: number,
  py: number,
  pz: number,
  bounds: CellBounds,
): void {
  if (!poly) return;
  poly.verts.push(toUV(px, py, pz, bounds));
}

export function moveFreeformVertex(
  idx: number,
  px: number,
  py: number,
  pz: number,
  bounds: CellBounds,
): void {
  if (!poly || idx < 0 || idx >= poly.verts.length) return;
  poly.verts[idx] = toUV(px, py, pz, bounds);
}

// Per-frame: update the rubber-band endpoint and which vertex (if any) the cursor
// is over (for the grab/close decision + the handle highlight).
export function setFreeformCursor(
  px: number,
  py: number,
  pz: number,
  bounds: CellBounds,
): void {
  if (!poly) return;
  const c = toUV(px, py, pz, bounds);
  poly.cursor = c;
  let best = -1;
  let bestD = HOVER_CELLS;
  for (let i = 0; i < poly.verts.length; i++) {
    const d = Math.hypot(c.u - poly.verts[i].u, c.v - poly.verts[i].v);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  poly.hoverVert = best >= 0 ? best : null;
}

// A press on the live plane: decide whether it grabs a vertex, closes the loop,
// or drops a new vertex. "close" tells the caller to fill (it owns the
// transaction). Recomputes the cursor/hover from the press point first.
export function freeformPress(
  px: number,
  py: number,
  pz: number,
  bounds: CellBounds,
): "drop" | "grab" | "close" | "none" {
  if (!poly) return "none";
  setFreeformCursor(px, py, pz, bounds);
  // Clicking the first vertex closes the loop (the wall-draw convention).
  if (poly.hoverVert === 0 && poly.verts.length >= 3) return "close";
  // Clicking any other placed vertex grabs it for a drag.
  if (poly.hoverVert !== null) {
    poly.dragVert = poly.hoverVert;
    return "grab";
  }
  dropFreeformVertex(px, py, pz, bounds);
  return "drop";
}

export function endFreeformDrag(): void {
  if (poly) poly.dragVert = null;
}

// Backspace: remove the most recently placed vertex (cancel once empty).
export function popFreeformVertex(): void {
  if (!poly) return;
  poly.verts.pop();
  if (poly.verts.length === 0) poly = null;
}

export function cancelFreeformPoly(): void {
  poly = null;
}

// Close the outline and fill (or erase/recolor) the area it encloses, into the
// object this polygon started on. The cell set is rasterized once and handed to
// reconcileFill (committed by the caller's transaction). Returns the cells a DRAW
// placed (so the caller can select them); empty for erase/recolor; null if there
// weren't enough vertices to enclose anything. Clears the polygon either way.
export function closeFreeformPoly(
  bounds: CellBounds,
  paletteIndex: PaletteIndex,
): { ix: number; iy: number; iz: number }[] | null {
  if (!poly) return null;
  if (poly.verts.length < 3) {
    poly = null;
    return null;
  }
  const cells = rasterizeFreeform(poly.verts, poly.planeAxis, poly.planeCoord, bounds);
  const kind: ShapeFillState["kind"] =
    poly.action === "draw"
      ? "freeform_draw"
      : poly.action === "erase"
        ? "freeform_erase"
        : "freeform_recolor";
  const state: ShapeFillState = {
    kind,
    applied: new Map(),
    placedKeys: new Set(),
  };
  reconcileFill(state, poly.grid, bounds, paletteIndex, () => cells);
  const drawn =
    poly.action === "draw"
      ? [...state.placedKeys].map((k) => {
          const p = k.split(",");
          return { ix: Number(p[0]), iy: Number(p[1]), iz: Number(p[2]) };
        })
      : [];
  poly = null;
  return drawn;
}

// gridGroup-local position of a plane point (u, v in cells), on the drawing plane.
function planePosition(u: number, v: number, out: number[]): void {
  if (!poly) return;
  const [uAxis, vAxis] = inPlaneAxes(poly.planeAxis);
  const planeY = (poly.planeCoord + 0.5) * CELL_SIZE;
  const xyz = [0, 0, 0];
  xyz[poly.planeAxis] = planeY;
  xyz[uAxis] = u * CELL_SIZE;
  xyz[vAxis] = v * CELL_SIZE;
  out.push(xyz[0], xyz[1], xyz[2]);
}

// The outline as gridGroup-local positions for the preview line main draws: the
// placed vertices, then the rubber-band to the cursor, plus a snap-back to the
// first vertex when the cursor is poised to close. Empty when not building.
export function freeformPreviewPositions(): number[] {
  if (!poly) return [];
  const out: number[] = [];
  for (const p of poly.verts) planePosition(p.u, p.v, out);
  if (poly.cursor) planePosition(poly.cursor.u, poly.cursor.v, out);
  if (poly.hoverVert === 0 && poly.verts.length >= 3 && poly.cursor) {
    planePosition(poly.verts[0].u, poly.verts[0].v, out);
  }
  return out;
}

// The placed vertices as gridGroup-local positions, for the handle dots.
export function freeformVertexPositions(): number[] {
  if (!poly) return [];
  const out: number[] = [];
  for (const p of poly.verts) planePosition(p.u, p.v, out);
  return out;
}

// Cells whose MAJORITY area lies inside the closed outline. Each candidate cell
// (over the path's bounding box, clamped to the canvas) is sampled on an N×N
// subgrid; it fills if more than half the samples are inside.
function rasterizeFreeform(
  path: Array<{ u: number; v: number }>,
  planeAxis: 0 | 1 | 2,
  planeCoord: number,
  bounds: CellBounds,
): Cell[] {
  if (path.length < 3) return [];
  const [uAxis, vAxis] = inPlaneAxes(planeAxis);

  let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
  for (const p of path) {
    if (p.u < minU) minU = p.u;
    if (p.u > maxU) maxU = p.u;
    if (p.v < minV) minV = p.v;
    if (p.v > maxV) maxV = p.v;
  }
  const cu0 = Math.max(axisLo(uAxis, bounds), Math.floor(minU));
  const cu1 = Math.min(axisHi(uAxis, bounds) - 1, Math.ceil(maxU) - 1);
  const cv0 = Math.max(axisLo(vAxis, bounds), Math.floor(minV));
  const cv1 = Math.min(axisHi(vAxis, bounds) - 1, Math.ceil(maxV) - 1);

  const out: Cell[] = [];
  const half = (SUB * SUB) / 2;
  for (let cv = cv0; cv <= cv1; cv++) {
    for (let cu = cu0; cu <= cu1; cu++) {
      let inside = 0;
      for (let j = 0; j < SUB; j++) {
        for (let i = 0; i < SUB; i++) {
          if (pointInPath(cu + (i + 0.5) / SUB, cv + (j + 0.5) / SUB, path)) inside++;
        }
      }
      if (inside > half) {
        const c: Cell = { ix: 0, iy: 0, iz: 0 };
        setAxis(c, planeAxis, planeCoord);
        setAxis(c, uAxis, cu);
        setAxis(c, vAxis, cv);
        out.push(c);
      }
    }
  }
  return out;
}

// Even-odd point-in-polygon over the path treated as a closed loop (a +U ray
// crossing count) — the same parity test the see-through wall shader uses.
function pointInPath(u: number, v: number, path: Array<{ u: number; v: number }>): boolean {
  let inside = false;
  for (let i = 0, j = path.length - 1; i < path.length; j = i++) {
    const a = path[i];
    const b = path[j];
    if (a.v > v !== b.v > v) {
      const cross = a.u + ((v - a.v) / (b.v - a.v)) * (b.u - a.u);
      if (cross > u) inside = !inside;
    }
  }
  return inside;
}
